import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Input, Button } from '../../components/ui';
import { AlertTriangle, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export function DuesSplitCalculatorTool() {
  const [totalDues, setTotalDues] = useState('100');
  const [nationalPct, setNationalPct] = useState('25');
  const [statePct, setStatePct] = useState('15');
  const [includeFees, setIncludeFees] = useState(true);
  const [feePercent, setFeePercent] = useState('2.9');
  const [feeFixed, setFeeFixed] = useState('0.30');
  const [offsetFees, setOffsetFees] = useState(false);
  
  const dues = parseFloat(totalDues) || 0;
  const nPct = parseFloat(nationalPct) || 0;
  const sPct = parseFloat(statePct) || 0;
  const pctFee = parseFloat(feePercent) || 0;
  const fixedFee = parseFloat(feeFixed) || 0;
  
  const localPct = Math.max(0, 100 - nPct - sPct);
  
  let receiptGross = dues;
  let computedGatewayFee = 0;
  let netPool = dues;

  if (includeFees) {
    const rate = pctFee / 100;
    if (offsetFees) {
      receiptGross = rate < 1 ? (dues + fixedFee) / (1 - rate) : dues + fixedFee;
      computedGatewayFee = receiptGross - dues;
      netPool = dues;
    } else {
      computedGatewayFee = (dues * rate) + fixedFee;
      netPool = Math.max(0, dues - computedGatewayFee);
      receiptGross = dues;
    }
  }

  const nAmt = (netPool * nPct) / 100;
  const sAmt = (netPool * sPct) / 100;
  const lAmt = (netPool * localPct) / 100;

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Dues & Fee Structure</h3>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-400">Total Member Dues ($)</label>
            <Input type="number" value={totalDues} onChange={e => setTotalDues(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">National Split (%)</label>
              <Input type="number" value={nationalPct} onChange={e => setNationalPct(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">State/Regional Split (%)</label>
              <Input type="number" value={statePct} onChange={e => setStatePct(e.target.value)} />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 dark:border-slate-800 space-y-4">
            <div className="flex items-start gap-2.5">
              <input
                id="includeFeesCheckbox"
                type="checkbox"
                checked={includeFees}
                onChange={e => setIncludeFees(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <div className="flex flex-col">
                <label htmlFor="includeFeesCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-305 cursor-pointer select-none">
                  Include credit card processing fees (e.g. Stripe)
                </label>
                <span className="text-xs text-gray-400">
                  Calculate the fee impact on the split distribution.
                </span>
              </div>
            </div>

            {includeFees && (
              <div className="pl-6 space-y-4 bg-slate-50 dark:bg-slate-900/40 p-4 rounded-xl border border-gray-100 dark:border-slate-800/80">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-400">Percentage Fee (%)</label>
                    <Input type="number" step="0.1" value={feePercent} onChange={e => setFeePercent(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-400">Fixed Fee ($)</label>
                    <Input type="number" step="0.01" value={feeFixed} onChange={e => setFeeFixed(e.target.value)} />
                  </div>
                </div>

                <div className="flex items-start gap-2.5 pt-2">
                  <input
                    id="offsetFeesCheckbox"
                    type="checkbox"
                    checked={offsetFees}
                    onChange={e => setOffsetFees(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div className="flex flex-col">
                    <label htmlFor="offsetFeesCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-305 cursor-pointer select-none">
                      Increase member dues to offset processing fees
                    </label>
                    <span className="text-xs text-gray-400">
                      Charge the member the increased gross amount so that the split payouts remain exactly as specified (${dues.toFixed(2)} net).
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card className="p-6 bg-slate-50 border-slate-200 dark:bg-slate-900/40 dark:border-slate-800 flex flex-col justify-between h-full">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 block">Split Breakdown</span>
            
            {includeFees ? (
              <div className="space-y-3 mb-6 bg-white dark:bg-slate-950 p-4 rounded-xl border border-gray-150 dark:border-slate-800">
                <div className="flex justify-between text-sm py-1">
                  <span className="text-slate-500 dark:text-slate-400 font-medium">Total Member Pay (Gross):</span>
                  <span className="font-mono font-bold text-slate-900 dark:text-white">${receiptGross.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm py-1 text-red-650 dark:text-red-400">
                  <span className="text-slate-500 dark:text-slate-400 font-medium">Gateway Fees (e.g. Stripe):</span>
                  <span className="font-mono font-bold">-${computedGatewayFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-gray-150 dark:border-slate-800 text-emerald-650 dark:text-emerald-400">
                  <span className="font-semibold">Net Distributable Pool:</span>
                  <span className="font-mono font-black text-base">${netPool.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3 mb-6 bg-white dark:bg-slate-950 p-4 rounded-xl border border-gray-150 dark:border-slate-800">
                <div className="flex justify-between text-sm py-1">
                  <span className="text-slate-500 dark:text-slate-400 font-medium">Total Member Dues:</span>
                  <span className="font-mono font-bold text-slate-900 dark:text-white">${dues.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-gray-150 dark:border-slate-800 text-slate-700 dark:text-slate-300">
                  <span className="font-semibold">Net Distributable Pool:</span>
                  <span className="font-mono font-black text-base">${dues.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Portion Distributions</span>
              <div className="space-y-3">
                <div className="flex justify-between text-sm py-2 border-b border-gray-200 dark:border-slate-700">
                  <span className="text-slate-600 dark:text-slate-300">National Headquarters ({nPct}%):</span>
                  <span className="font-mono font-bold">${nAmt.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm py-2 border-b border-gray-200 dark:border-slate-700">
                  <span className="text-slate-600 dark:text-slate-300">State/Region ({sPct}%):</span>
                  <span className="font-mono font-bold">${sAmt.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm py-2 border-b border-gray-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                  <span className="text-blue-800 dark:text-blue-300 font-bold">Local Chapter ({localPct}%):</span>
                  <span className="font-mono font-bold text-blue-600 dark:text-blue-400">${lAmt.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-slate-800">
              <div className="p-4 bg-[#FFD600] text-black border-2 border-black space-y-2">
                <p className="text-xs font-bold leading-snug">
                  Want to automate this exact split? Connect Stripe on OrgSets and route {nPct}% to National and {localPct}% to Local chapters automatically on every payment.
                </p>
                <Link
                  to="/sign-up"
                  className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider bg-black text-white hover:bg-[#0044FF] px-3 py-1.5 transition-colors cursor-pointer"
                >
                  Create Free Organization <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

